import React, {useContext} from 'react'
import PropTypes from 'prop-types'
import {MobileSwitchProvider} from 'hooks/MobileSwitch'
import {
  Accessibility,
  Accordion,
  BusinessHours,
  Cooperations,
  Costs,
  DetailLinkList,
  Equipment,
  FacilityInformation,
  Facts,
  FeatureList,
  GetToKnow,
  HomeContract,
  InsuredOffers,
  LinkList,
  LocalisationBox,
  Locations,
  MealInformation,
  NursingCalculator,
  Offers,
  PatientsExperienceQuestionnaire,
  PdfReport,
  Quality,
  QualityLabel,
  QualityManagement,
  QualityTransparency,
  ReligiousOffers,
  Slider,
  Staff,
  TextLinkImage,
  TreatmentOptions,
  WorkplaceHealthPromotion,
} from 'components/molecules'
import {DetailContentWrapper} from '../'
import eprdLogo from 'assets/img/eprd_logo.jpg'
import {ContactPerson, Link, VisitingHours} from 'components/atoms'
import {
  CancerCenters,
  MinAmountRegulation,
  QualityIndication,
  StructuredQualityReport,
} from 'components/molecules/Quality/partials'

// libs && helpers
import {TextContext} from 'hocs'
import {__dev__, BOOKABLE_TYPES as RESULT_TYPES, entryPointId, searchTypeIds,} from 'settings/app/constants'
import DOMPurify from 'dompurify'
import {useSelector} from 'react-redux'
import {validCoordinates} from 'settings/app/helpers'
import {getKoroDeepLink} from 'settings/app/urlHelpers'
import NursingIndicator from 'components/molecules/NursingIndicator/NursingIndicator'
import QualityIndicators from 'components/molecules/QualityIndicators/QualityIndicators'
import featureToggles from 'feature-toggles'

const DetailContentFactory = ({
  detailsKey,
  itemData,
  backLink,
  isTreatmentReason,
  entryPoint,
  resultData,
}) => {
  const textContext = useContext(TextContext)
  const searchParams = useSelector((state) => state.search.params)

  const replaceIconPlaceholder = (text, iconName) =>
    text.replace(
      '__ICON_PLACEHOLDER__',
      `<span class='icon-grau1 icon-size-16 ${ iconName }' />`
    )

  const getKoroParams = () => {
    let redirectURL
    const koroParams = {}

    if (__dev__) {
      redirectURL = `/${ searchTypeIds.HOSPITALS }`
    } else {
      redirectURL = `${ localStorage.getItem(
        `${ entryPointId.HOSPITALS }SearchURL`
      ) }`
    }

    if (searchParams.location !== '') {
      koroParams.location = searchParams.location
      koroParams.radius = searchParams.radius
    }

    return { redirectURL, koroParams }
  }

  /**
   * with title && additional text
   */
  const getFullFeatureList = () => {
    const checkedListElements =
      entryPoint === entryPointId.CARESUPPORT ||
      entryPoint === entryPointId.DAYANDNIGHTCARE
    const { lists } = itemData

    return (
      <DetailContentWrapper>
        {itemData.copy && (
          <p
            dangerouslySetInnerHTML={{
              __html: replaceIconPlaceholder(
                DOMPurify.sanitize(itemData.copy),
                'icon-f-info-circle-full'
              ),
            }}
          />
        )}
        <FeatureList
          itemList={ lists }
          { ...(checkedListElements ? { listClass: 'list-checkmark' } : {}) }
        />
      </DetailContentWrapper>
    )
  }

  /**
   * with out title and additional text
   */
  const getMinFeatureList = () => (
    <FeatureList
      featureKey={ detailsKey }
      itemList={ itemData }
      listClass={ 'list-checkmark' }
    />
  )

  const getAccordionContent = () => {
    const url = new URL(window.location)

    switch (detailsKey) {
      case 'peq':
        // breakpoint MD is required for TabNav at PEQ
        return (
          <MobileSwitchProvider breakpoint={ 'md' }>
            <PatientsExperienceQuestionnaire
              defaultData={ itemData.default }
              departmentsData={ itemData.departments }
              additionalInfo={ itemData.additionalInfo }
              noValueText={
                itemData.hasOwnProperty('noValueText') &&
                itemData.noValueText !== ''
                  ? itemData.noValueText
                  : false
              }
            />
          </MobileSwitchProvider>
        )

      case 'locationInfo':
      case 'locations':
        const searchKey = url.searchParams.get('key')
        const searchType = url.searchParams.get('type')

        const coordinates = {
          lat: parseFloat(url.searchParams.get('lat')),
          lng: parseFloat(url.searchParams.get('lng')),
        }

        if (!validCoordinates(coordinates.lat, coordinates.lng)) {
          coordinates.lat = false
          coordinates.lng = false
        }

        return (
          <Locations
            type={
              'locationInfo' === detailsKey ? 'singleLocation' : 'multiLocation'
            }
            locations={ itemData }
            entryPoint={ entryPoint }
            searchKey={ searchKey }
            searchType={ searchType }
            coordinates={ coordinates }
            additionalInfo={ itemData.additionalInfo }
          />
        )

      case 'departmentsList':
        return (
          <DetailContentWrapper>
            <LinkList items={ itemData } backLink={ backLink } />
          </DetailContentWrapper>
        )

      case 'cooperations':
        let listOfCooperations = []
        if (entryPoint === entryPointId.CAREHOMES) {
          listOfCooperations = itemData.cooperations
        } else {
          // for doctors itemData is an array
          listOfCooperations = !Array.isArray(itemData) ? [itemData] : itemData
        }

        return (
          <DetailContentWrapper>
            <Cooperations
              contact={ itemData?.contact }
              cooperations={ listOfCooperations }
            />
          </DetailContentWrapper>
        )

      case 'contactPersons':
      case 'branches':
        const validContactPersons = itemData.filter(
          (contactPerson) => !!contactPerson?.title && !!contactPerson?.address
        )

        return (
          <DetailContentWrapper>
            {detailsKey === 'contactPersons' && (
              <b>{textContext['contactPersons.subTitle']}</b>
            )}
            {validContactPersons.map((contactPerson, key) => (
              <ContactPerson key={ key } contactPerson={ contactPerson } />
            ))}
          </DetailContentWrapper>
        )

      case 'doctors':
        // for doctors data is an array
        const listDataDoctors = !Array.isArray(itemData) ? [itemData] : itemData
        return (
          <DetailContentWrapper>
            {listDataDoctors.map((item, index) => (
              <DetailLinkList
                key={ index }
                headline={ item.headline }
                copy={ item.copy }
                items={ item.list }
              />
            ))}
          </DetailContentWrapper>
        )

      case 'insuredBenefits':
        // #1 - if just one item show only text with headline, copy, optional link
        // #2 - if array of items show accordionList

        // get filtered benefits (added in detailsReducer!) 'Filtered'
        // const filteredData = resultData.details[`${ detailsKey }Filtered`]
        return (
          <DetailContentWrapper>
            {/* back link only for hospitals without treatment reason */}
            {!isTreatmentReason && entryPoint === entryPointId.HOSPITALS && (
              <p>
                <Link
                  id={ backLink.id }
                  to={ backLink.to }
                  className='link back-link'>
                  {textContext['navigation.backToSearchTreatment']}
                </Link>
              </p>
            )}

            <LocalisationBox id={ detailsKey } client={ itemData.clientName } />

            {/* if just one item exist - text, headline, link component*/}
            {itemData.items.length === 1 && (
              <TextLinkImage
                headline={ itemData.items[0].headline }
                copy={ itemData.items[0].copy }
                moreLink={
                  itemData.items[0].moreLink
                    ? {
                        url: itemData.items[0].moreLink,
                        target: '_blank',
                      }
                    : null
                }
              />
            )}

            {/* show list of item if more than one item exist*/}
            {itemData.items.length > 1 &&
              itemData.items.map((item, index) => (
                <Accordion
                  key={ index }
                  label={ item.headline }
                  type={ 'UNDERLINED' }
                  className={
                    index === itemData.items.length - 1
                      ? 'spacing-bottom-large'
                      : 'spacing-bottom'
                  }>
                  <p className={ 'copy' }>{item.copy}</p>
                  {item.moreLink && (
                    <Link
                      href={ item.moreLink }
                      isNativeLink={ true }
                      target={ '_blank' }
                      className={ 'link more-link spacing-bottom' }
                      rel={ 'noopener noreferrer' }>
                      {textContext['general.exploreMore']}
                    </Link>
                  )}
                </Accordion>
              ))}
          </DetailContentWrapper>
        )

      case 'qualityTransparency':
        return (
          <DetailContentWrapper>
            <TextLinkImage
              headline={ itemData.headline }
              copy={ itemData.copy }
              image={{
                url: eprdLogo,
                alt: textContext['eprd.logo.title'],
              }}
              moreLink={{
                url: itemData.moreLink,
                target: '_blank',
              }}
            />
          </DetailContentWrapper>
        )

      case 'languages':
      case 'visitingHours':
        return (
          <DetailContentWrapper>
            <VisitingHours data={ itemData } />
          </DetailContentWrapper>
        )

      case 'slideshow':
      case 'slideshowOffice':
        return (
          <DetailContentWrapper>
            <Slider slides={ itemData } />
          </DetailContentWrapper>
        )

      case 'quality':
        return (
          <DetailContentWrapper printAsBlock={ true }>
            {!isTreatmentReason && (
              <div style={{ paddingBottom: 10 }}>
                <Link
                  id='back-link'
                  to={ backLink.to }
                  className='link more-link'>
                  {textContext['detailView.hospital.backlinkTxt']}
                </Link>
              </div>
            )}

            {itemData.koro && (
              <p
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    itemData.koro.replace(
                      '_HIER_',
                      getKoroDeepLink(getKoroParams())
                    )
                  ),
                }}
              />
            )}

            {itemData.qsr && (
              <Quality
                isTreatmentReason={ isTreatmentReason }
                type='qsr'
                tableData={ itemData.qsr }
              />
            )}

            {itemData.iqtig && (
              <Quality
                isTreatmentReason={ isTreatmentReason }
                type='iqtig'
                tableData={ itemData.iqtig }
              />
            )}

            {itemData?.qsr?.additionalInfo && (
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(itemData.qsr.additionalInfo),
                }}
              />
            )}
            {/* do not show SQR from 01.02.2021 until new SQR url is set */}
            {itemData.structuredQualityReport && (
              <StructuredQualityReport
                urlToPdf={ itemData.structuredQualityReport.url }
              />
            )}
            {itemData.cancerCenters && (
              <CancerCenters data={ itemData.cancerCenters } />
            )}
            {itemData.minimumAmountRegulation &&
              Object.keys(itemData.minimumAmountRegulation).length > 0 && (
                <MinAmountRegulation data={ itemData.minimumAmountRegulation } />
              )}
          </DetailContentWrapper>
        )

      case 'qualityIndication':
        return (
          <DetailContentWrapper>
            <QualityIndication qualityIndication={ itemData.data } />
          </DetailContentWrapper>
        )

      case 'religiousOffers':
        if (entryPoint === entryPointId.HOSPITALS) {
          return (
            <DetailContentWrapper>{getMinFeatureList()}</DetailContentWrapper>
          )
        }
        return (
          <DetailContentWrapper>
            <ReligiousOffers data={ itemData } />
          </DetailContentWrapper>
        )

      case 'childCare':
      case 'eatingHabits':
        return (
          <DetailContentWrapper>{getMinFeatureList()}</DetailContentWrapper>
        )

      // room features day and night care has headlines
      case 'roomFeatures':
        return entryPoint === entryPointId.DAYANDNIGHTCARE ? (
          getFullFeatureList()
        ) : (
          <DetailContentWrapper>{getMinFeatureList()}</DetailContentWrapper>
        )

      case 'qualifications':
      case 'services':
        return getFullFeatureList()

      case 'mealInformation':
        return (
          <DetailContentWrapper>
            <MealInformation mealInformation={ itemData } />
          </DetailContentWrapper>
        )
      case 'getToKnow':
        return (
          <DetailContentWrapper>
            <GetToKnow getToKnow={ itemData } />
          </DetailContentWrapper>
        )

      case 'treatmentOptions':
        return (
          <MobileSwitchProvider breakpoint={ 'md' }>
            <TreatmentOptions items={ itemData } />
          </MobileSwitchProvider>
        )

      case 'facts':
        // sort the items after treatments cases descending
        itemData.sort((a, b) => b.treatments.cases - a.treatments.cases)
        return (
          <MobileSwitchProvider breakpoint={ 'md' }>
            <Facts items={ itemData } />
          </MobileSwitchProvider>
        )

      case 'accessibility':
        return (
          <MobileSwitchProvider breakpoint={ 'md' }>
            <Accessibility items={ itemData } type={ entryPoint } />
          </MobileSwitchProvider>
        )

      case 'businessHours':
        return (
          <BusinessHours
            data={ itemData }
            openingIndicatorData={ resultData.openingIndicator }
            appointmentUrl={ resultData.appointmentUrl }
            entryPoint={ entryPoint }
          />
        )

      case 'qualityLabel':
        return (
          <DetailContentWrapper>
            <QualityLabel itemList={ itemData } />
          </DetailContentWrapper>
        )

      case 'prices':
        const selectedFocus =
          url.searchParams.get('carehomes_focus') ||
          url.searchParams.get('PR_TYP') ||
          ''

        return (
          <DetailContentWrapper>
            {Object.keys(itemData).map((key, index) =>
              itemData[key].careFocus.length > 0 ? (
                <NursingCalculator
                  data={ itemData[key] }
                  type={ key }
                  key={ index }
                  detailId={ resultData?.id }
                  preSelectedFocus={ selectedFocus }
                />
              ) : null
            )}
          </DetailContentWrapper>
        )

      case 'facilityInformation':
        return (
          <DetailContentWrapper>
            <FacilityInformation
              contactData={ resultData.contactPersons }
              infoData={ itemData }
            />
          </DetailContentWrapper>
        )

      case 'qualityManagement':
        return (
          <DetailContentWrapper>
            <QualityManagement
              complaintManagement={ itemData.complaintManagement }
              report={ itemData.report }
              standards={ itemData.standards }
            />
          </DetailContentWrapper>
        )

      case 'pdfReport':
        return (
          <DetailContentWrapper>
            <PdfReport link={ itemData } />
          </DetailContentWrapper>
        )

      case 'equipment':
        return (
          <DetailContentWrapper>
            <Equipment { ...itemData } />
          </DetailContentWrapper>
        )

      case 'dailyRoutine':
        return (
          <DetailContentWrapper>
            <p
              dangerouslySetInnerHTML={{
                __html: itemData,
              }}
            />
          </DetailContentWrapper>
        )

      case 'offers':
        return (
          <DetailContentWrapper>
            <Offers data={ itemData } />
          </DetailContentWrapper>
        )

      case 'insuredOffers':
        return (
          <DetailContentWrapper>
            <InsuredOffers offers={ itemData } />
          </DetailContentWrapper>
        )

      case 'staff':
        return (
          <DetailContentWrapper>
            <Staff staff={ itemData } />
          </DetailContentWrapper>
        )

      case 'homeContract':
        return (
          <DetailContentWrapper>
            <HomeContract { ...itemData } />
          </DetailContentWrapper>
        )

      case 'costs':
        const selectedType = url.searchParams.get('PE_ANG_KRANKENPFLEGE') || ''

        // hide medical care costs
        return selectedType === '003' ? null : (
          <DetailContentWrapper>
            <Costs
              onCalculateLinkClick={ () => console.log('open cost overlay') }
              sampleProfiles={ itemData.sampleProfiles }
              userProfiles={ itemData.userProfiles }
            />
          </DetailContentWrapper>
        )

      case 'qualityReport':
        const { quality } = resultData.details
        return (
          <DetailContentWrapper>
            {quality?.qualityIndicators && featureToggles.qualityIndicators && (
              <QualityIndicators { ...quality.qualityIndicators } />
            )}
            {quality?.external && (
              <NursingIndicator variant='square' { ...quality.external } />
            )}
            <QualityTransparency { ...itemData } />
          </DetailContentWrapper>
        )

      case 'workplaceHealthPromotion':
        return (
          <DetailContentWrapper>
            <WorkplaceHealthPromotion { ...itemData } />
          </DetailContentWrapper>
        )

      default:
        return null
    }
  }

  return <>{getAccordionContent()}</>
}

DetailContentFactory.propTypes = {
  detailsKey: PropTypes.string,
  entryPoint: PropTypes.oneOf([...RESULT_TYPES]).isRequired,
  itemData: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.string,
  ]),
  resultData: PropTypes.object,
  isTreatmentReason: PropTypes.bool,
  backLink: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
}

export default DetailContentFactory
